import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  orderBy,
  Timestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { ProjectName } from '../../types';

const PROJECT_NAMES_COLLECTION = 'projectNames';

export const projectNamesService = {
  // Get all project names
  async getProjectNames(): Promise<ProjectName[]> {
    try {
      const q = query(collection(db, PROJECT_NAMES_COLLECTION), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        claimedDate: doc.data().claimedDate?.toDate() || undefined,
        deletedDate: doc.data().deletedDate?.toDate() || undefined,
      })) as ProjectName[];
    } catch (error) {
      console.error('Error fetching project names:', error);
      throw error;
    }
  },

  // Add a new project name
  async addProjectName(projectNameData: Omit<ProjectName, 'id' | 'createdAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, PROJECT_NAMES_COLLECTION), {
        ...projectNameData,
        createdAt: Timestamp.now(),
        claimedDate: projectNameData.claimedDate ? Timestamp.fromDate(projectNameData.claimedDate) : null,
        deletedDate: projectNameData.deletedDate ? Timestamp.fromDate(projectNameData.deletedDate) : null,
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding project name:', error);
      throw error;
    }
  },

  // Update project name (for claiming/releasing)
  async updateProjectName(projectNameId: string, updates: Partial<ProjectName>): Promise<void> {
    try {
      const projectNameRef = doc(db, PROJECT_NAMES_COLLECTION, projectNameId);
      const updateData = { ...updates };
      
      // Convert dates to Timestamps
      if (updateData.claimedDate) {
        updateData.claimedDate = Timestamp.fromDate(updateData.claimedDate) as any;
      }
      if (updateData.deletedDate) {
        updateData.deletedDate = Timestamp.fromDate(updateData.deletedDate) as any;
      }
      
      await updateDoc(projectNameRef, updateData);
    } catch (error) {
      console.error('Error updating project name:', error);
      throw error;
    }
  },

  // Claim a project name
  async claimProjectName(projectNameId: string, userEmail: string): Promise<void> {
    return this.updateProjectName(projectNameId, {
      claimed: true,
      claimedBy: userEmail,
      claimedDate: new Date(),
    });
  },

  // Release a project name
  async releaseProjectName(projectNameId: string): Promise<void> {
    try {
      const projectNameRef = doc(db, PROJECT_NAMES_COLLECTION, projectNameId);
      await updateDoc(projectNameRef, {
        claimed: false,
        claimedBy: deleteField(),
        claimedDate: deleteField(),
      });
    } catch (error) {
      console.error('Error releasing project name:', error);
      throw error;
    }
  },

  // Soft delete a project name
  async deleteProjectName(projectNameId: string, userEmail: string): Promise<void> {
    try {
      const projectNameRef = doc(db, PROJECT_NAMES_COLLECTION, projectNameId);
      await updateDoc(projectNameRef, {
        deleted: true,
        deletedBy: userEmail,
        deletedDate: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error deleting project name:', error);
      throw error;
    }
  },

  // Restore a deleted project name
  async restoreProjectName(projectNameId: string): Promise<void> {
    try {
      const projectNameRef = doc(db, PROJECT_NAMES_COLLECTION, projectNameId);
      await updateDoc(projectNameRef, {
        deleted: false,
        deletedBy: deleteField(),
        deletedDate: deleteField(),
      });
    } catch (error) {
      console.error('Error restoring project name:', error);
      throw error;
    }
  },

  // Permanently delete a project name (admin/owner only)
  async permanentlyDeleteProjectName(projectNameId: string): Promise<void> {
    try {
      const projectNameRef = doc(db, PROJECT_NAMES_COLLECTION, projectNameId);
      await updateDoc(projectNameRef, {
        permanentlyDeleted: true,
        permanentlyDeletedDate: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error permanently deleting project name:', error);
      throw error;
    }
  },
};